import type { tTikTokRichContent } from 'src/pages/TikTok/TikTok.types';
import { promptOf } from 'src/components/LeadClose/LeadClose.functions';
import type { tCloseType } from 'src/components/LeadClose/LeadClose.types';
import { useMemo } from 'react';
import { isAxiosError } from 'axios';
import { httpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from 'src/utils/resolveApiError';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
  lookupOutboundRefusalByCode,
  tOutboundRefusal,
} from 'src/utils/ErrorDirectory';
import { firebase } from '../../firebase/firebase.singleton.ts';
import {
  FileUploadService,
  isNetworkUnreachableError,
  resolveEffectiveMimeType,
} from '../../services/FileUpload/FileUpload.service.ts';
import { tUpdateLeadRequest } from '../../pages/Pipe/Pipe.types.ts';
import { useIntegrationsContext } from '../../providers/IntegrationsProvider/Integrations.context.ts';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context.ts';
import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums.ts';
import { tChatEventContentAttachments } from '../../providers/SocketsProvider/Sockets.types.ts';
import { eAlertType } from '../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../providers/ToastProvider/Toast.hook.ts';
import {
  tAlert,
  tToastAlert,
} from '../../providers/ToastProvider/Toast.types.ts';
import { MediaCompressionService } from 'src/services/MediaCompression/MediaCompression.service';
import { isTranscodeOnlyImage } from 'src/services/MediaCompression/MediaCompression.functions';
import {
  HEIC_TRANSCODE_TIMEOUT_MS,
  MAX_HEIC_INPUT_HUMAN,
} from 'src/services/MediaCompression/MediaCompression.constants';
import { eCompressionFailReason } from 'src/services/MediaCompression/MediaCompression.types';
import { sentry } from 'src/sentry/sentry.singleton';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context.ts';
import { getRecordedAudioMimetype } from '../../utils/GetRecordedAudioMimetype.ts';
import { downloadMessageAttachment } from '../../utils/legacyDecrypt.ts';

import { eAvailabilityPromptReason } from 'src/hooks/availability/availabilityPrompt.enum';
import { requestAvailabilityPrompt } from 'src/hooks/availability/useAvailabilityPrompt.hook';
import { MediaType } from './Chat.enum.ts';
import { useChatContext } from './Chat.hook.ts';
import { ChatService } from './Chat.service.ts';
import {
  tAttachment,
  tChatEvent,
  tDriveFile,
  tLocationPlace,
  tSendMessageRequest,
  tTextSendResult,
} from './Chat.types.ts';

import {
  WapiPlanSendBlockedError,
  buildLocationAttachment,
  shouldBlockSendToReadOnlyChannel,
  withDecryptedUrl,
} from './Chat.actions.functions.ts';
import { useWapiPlanSendGuard } from './useWapiPlanSendGuard.hook.ts';
import { useMessageBarContext } from './components/MessageBar/MessageBar.hook.ts';
import {
  getMaxFileSizeForChannel,
  isEmptyFile,
} from './components/MessageBar/components/ToolActions/components/DriveFilesAction/components/UploadFileAction/UploadFileAction.constants.ts';
import { EmptyFileError } from '../../services/errors/EmptyFileError.ts';
import useMessageBubble from './components/MessageContainer/components/EventBubble/components/MessageBubble/MessageBubble.hook.tsx';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import {
  recordBlockHumanAction,
  tHumanActionChat,
  withHumanAction,
} from './Chat.humanAction.ts';

const functions = firebase.functions;

/**
 * Single outbound-send seam for this file (RET-1350). Every send action here —
 * text, location, audio, file, Meta template — routes through it so the
 * availability prompt has exactly one trigger point instead of six call sites
 * that a future send action could silently forget to update.
 *
 * The announcement runs only after the send resolves, so a failed send never
 * asks the seller about their status, and it never gates the send itself: the
 * message is already on its way before the prompt can render.
 */
async function sendMessageAndAnnounce(
  request: tSendMessageRequest,
  workspaceId: string | undefined,
  chat: tHumanActionChat,
) {
  // INT-1538 U10 (KD2): a manual send is a person acting on the chat, so the
  // human action is recorded first, then the message goes out regardless.
  return withHumanAction(workspaceId, chat, async () => {
    const response = await ChatService.sendMessage(request);
    requestAvailabilityPrompt(eAvailabilityPromptReason.MESSAGE_SENT);
    return response;
  });
}

/**
 * Copy for a media file that could not be made sendable (BUG-880). The size
 * message only fits a file that was already over the limit: an under-limit
 * failure is a format the conversion could not produce, whatever stage gave up.
 */
function pickNormalizationFailureKey(options: {
  reason: eCompressionFailReason;
  isOversize: boolean;
}): string {
  if (options.reason === eCompressionFailReason.NO_VIDEO_TRACK) {
    return 'driveFilesAction.audioOnlyVideo';
  }
  return options.isOversize
    ? 'driveFilesAction.fileSizeExceeded'
    : 'driveFilesAction.formatConversionFailed';
}

export default function useChatActions() {
  const { t } = useTranslation(['chat/chat']);
  const { t: tErrors } = useTranslation('errors/errors');
  const deleteDocumentFromWorkspaceDrive = useMemo(
    () => httpsCallable(functions, 'deleteDocumentFromWorkspaceDrive'),
    [],
  );
  // Publishes to the shared folder only when the upload ticket wrote no record
  // (see the share step in sendFileMessage); otherwise the record is shared
  // with a PATCH.
  const addDocumentToWorkspaceDrive = useMemo(
    () => httpsCallable(functions, 'addDocumentToWorkspaceDrive'),
    [],
  );

  const { currentUserId } = useUserContext();
  const { addAlert, deleteAlert } = useToastContext();
  const { currentWorkspace, currentWorkspaceId } = useWorkspaceContext();
  const {
    selectedChat,
    replyMessage,
    setReplyMessage,
    resendIds,
    textAreaRef,
    whatsAppWebConnection,
  } = useChatContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { hasWaWebAccount, hasWaBusinessIntegration } =
    useIntegrationsContext();
  const { replaceMessageWithVariable } = useMessageBubble();
  const { setMessageBarValue } = useMessageBarContext();
  // RET-2339 (D6) / RET-2349 (KTD2): the shared plan guard. A send out of a
  // WhatsApp API chat whose plan has no WhatsApp API shows one toast and
  // bails out; keyed on the RESOLVED channel, like the read-only guard.
  const { blockWapiPlanSend, isWapiPlanSendBlocked } = useWapiPlanSendGuard();

  /**
   * Defense-in-depth pre-send guard (INT-941 / U3). Called at the top of every
   * send / forward function. When the target channel is a WhatsApp Web channel
   * whose account no longer exists, it surfaces a clear, explanatory toast and
   * returns `true` so the caller bails out before dispatching — instead of the
   * silent provider failure (error 1003) and the generic red error bar.
   *
   * It does NOT await `whatsAppWebConnection` (the live U1 fetch): an
   * unresolved `pending` degrades to the cached `hasWaWebAccount()` hint inside
   * `shouldBlockSendToReadOnlyChannel`, so a send is never blocked waiting on a
   * round-trip. Non-WhatsApp-Web channels always pass through.
   */
  const isSendToReadOnlyChannelBlocked = (channel: eChannelType): boolean => {
    const blocked = shouldBlockSendToReadOnlyChannel({
      channel,
      whatsAppWebPhase: whatsAppWebConnection.phase,
      hasWaWebAccount: hasWaWebAccount(),
    });

    if (blocked) {
      // U3 keys the guard on the RESOLVED channel (`selectedChat.integration`),
      // while the U2 read-only composer keys on the REQUESTED integration. They
      // diverge in the BUG-336 doubly-disconnected edge (WAPI requested but no
      // WABA -> resolved to WHATSAPP_WEB, and no WhatsApp Web account either):
      // the composer renders editable yet every send is blocked here. Pick a
      // role-neutral toast when there is no WhatsApp Business channel, so the
      // message never tells the agent to switch to a dropdown option that is
      // not rendered. (INT-941 review.)
      addAlert({
        type: eAlertType.ERROR,
        message: hasWaBusinessIntegration()
          ? t('whatsAppWebReadOnly.guardToast')
          : t('whatsAppWebReadOnly.guardToastNoWaba'),
      });
    }

    return blocked;
  };

  /**
   * The toast for a failed send. While the workspace's plan is paused the
   * server refuses the send with 423 and creates no message, so this toast is
   * the only place the seller learns why: the paused copy for a paused plan,
   * neutral copy for any other refusal, instead of axios's "Request failed
   * with status code 423". Any other failure keeps the message it had.
   */
  const sendFailureAlert = (options: {
    error: unknown;
    fallback: string;
  }): tAlert => {
    const refusal = lookupOutboundRefusal(options.error);
    if (refusal) {
      return buildOutboundRefusalAlert(refusal, tErrors);
    }
    return {
      type: eAlertType.ERROR,
      message: (options.error as Error).message || options.fallback,
    };
  };

  const sendTikTokRichMessage = async (
    content: tTikTokRichContent,
    id: string,
  ): Promise<boolean> => {
    if (
      !currentWorkspace?.id ||
      !selectedChat?.id ||
      selectedChat.integration !== eChannelType.TIKTOK ||
      !currentUserId ||
      isSendToReadOnlyChannelBlocked(eChannelType.TIKTOK)
    )
      return false;
    if (blockWapiPlanSend(eChannelType.TIKTOK)) return false;
    await sendMessageAndAnnounce(
      {
        chatId: selectedChat.id,
        data: {
          id,
          channel_type: eChannelType.TIKTOK,
          content: { tiktok: content },
          is_template: false,
          created_by: currentUserId,
          workspace_id: currentWorkspace.id,
        },
      },
      currentWorkspaceId,
      selectedChat,
    );
    return true;
  };

  const sendTextMessage = async (message: string): Promise<tTextSendResult> => {
    if (
      !message ||
      !currentWorkspace?.id ||
      !selectedLeadForChatModal?.handle ||
      !currentUserId ||
      !selectedChat?.id
    ) {
      throw new Error('No se puede enviar el mensaje');
    }

    const channelType = selectedChat?.integration ?? eChannelType.WHATSAPP_WEB;
    if (isSendToReadOnlyChannelBlocked(channelType)) {
      return { blockedByPlan: false, sent: false };
    }
    // RET-2349 (R1): no send out of a WhatsApp API chat whose plan has no
    // WhatsApp API. One toast, then return without sending. (R4): the flag
    // tells the caller nothing was sent, so it keeps the text.
    if (blockWapiPlanSend(channelType)) {
      return { blockedByPlan: true, sent: false };
    }

    try {
      const newMessage = {
        id: uuidv4(),
        channel_type: channelType,
        content: {
          text: replaceMessageWithVariable(message),
          quoted: replyMessage?.id ?? undefined,
        },
        is_template: false,
        created_by: currentUserId,
        workspace_id: currentWorkspace?.id,
      };

      await sendMessageAndAnnounce(
        {
          chatId: selectedChat.id,
          data: newMessage,
        },
        currentWorkspaceId,
        selectedChat,
      );
      if (replyMessage?.id) {
        setReplyMessage(null);
      }
      setMessageBarValue('');
      return { blockedByPlan: false, sent: true };
    } catch (error) {
      addAlert(
        sendFailureAlert({
          error,
          fallback: 'Ocurrió un error al enviar el mensaje',
        }),
      );
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
      return { blockedByPlan: false, sent: false };
    } finally {
      textAreaRef.current?.focus();
    }
  };

  const sendLocationMessage = async (
    latitude: number,
    longitude: number,
    place?: tLocationPlace | null,
  ) => {
    if (
      !currentWorkspace?.id ||
      !selectedLeadForChatModal?.handle ||
      !currentUserId ||
      !selectedChat?.id
    ) {
      throw new Error('No se puede enviar el mensaje');
    }
    const channelType = selectedChat.integration ?? eChannelType.WHATSAPP_WEB;
    if (isSendToReadOnlyChannelBlocked(channelType)) {
      return;
    }
    // RET-2349 (R1): no send out of a WhatsApp API chat whose plan has no
    // WhatsApp API. One toast, then return without sending.
    if (blockWapiPlanSend(channelType)) {
      return;
    }
    try {
      const newMessage = {
        id: uuidv4(),
        channel_type: channelType,
        content: {
          quoted: replyMessage?.id ?? undefined,
          attachments: [
            buildLocationAttachment(latitude, longitude, channelType, place),
          ],
        },
        is_template: false,
        created_by: currentUserId,
        workspace_id: currentWorkspace?.id,
      };

      await sendMessageAndAnnounce(
        {
          chatId: selectedChat.id,
          data: newMessage,
        },
        currentWorkspaceId,
        selectedChat,
      );

      setMessageBarValue('');
    } catch (error) {
      addAlert(
        sendFailureAlert({
          error,
          fallback: 'Ocurrió un error al enviar el mensaje',
        }),
      );
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
    } finally {
      textAreaRef.current?.focus();
    }
  };

  const sendAudioMessage = async (audio: string) => {
    if (
      !audio ||
      !currentWorkspace?.id ||
      !selectedLeadForChatModal?.handle ||
      !currentUserId ||
      !selectedChat?.id
    ) {
      throw new Error('No se puede enviar el mensaje');
    }
    const channelType = selectedChat.integration ?? eChannelType.WHATSAPP_WEB;
    if (isSendToReadOnlyChannelBlocked(channelType)) {
      return;
    }
    // RET-2349 (R1): no send out of a WhatsApp API chat whose plan has no
    // WhatsApp API. One toast, then return without sending.
    if (blockWapiPlanSend(channelType)) {
      return;
    }
    try {
      const newMessage = {
        id: uuidv4(),
        channel_type: channelType,
        content: {
          quoted: replyMessage?.id ?? undefined,
          attachments: [
            {
              media_type: MediaType.AUDIO,
              mimetype: getRecordedAudioMimetype(audio),
              url: audio,
            },
          ],
        },
        is_template: false,
        created_by: currentUserId,
        workspace_id: currentWorkspace?.id,
      };

      await sendMessageAndAnnounce(
        {
          chatId: selectedChat.id,
          data: newMessage,
        },
        currentWorkspaceId,
        selectedChat,
      );

      setMessageBarValue('');
    } catch (error) {
      addAlert(
        sendFailureAlert({
          error,
          fallback: 'Ocurrió un error al enviar el mensaje',
        }),
      );
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
    } finally {
      textAreaRef.current?.focus();
    }
  };

  /**
   * Maps every reason normalizeImageForCompatibility can return to copy that is
   * actually true. Exhaustive on purpose: a catch-all default previously told a
   * user whose photo merely exceeded the input cap to "convert it to JPG or
   * PNG", which cannot help — HEIC is already denser than JPEG, so converting
   * makes the file bigger.
   */
  function heicFailureMessage(
    reason: eCompressionFailReason,
    fileName: string,
  ): string {
    switch (reason) {
      case eCompressionFailReason.ENCODER_UNAVAILABLE:
        // The file is fine; the decoder is not. Reloading rebuilds heic-to's
        // shared worker, which is the only way out of a wedged decode.
        return t('driveFilesAction.imageProcessorUnavailable', { fileName });
      case eCompressionFailReason.INPUT_TOO_LARGE:
        return t('driveFilesAction.fileSizeExceeded', {
          fileName,
          maxSize: MAX_HEIC_INPUT_HUMAN,
        });
      default:
        return t('driveFilesAction.unsupportedImageFormat', { fileName });
    }
  }

  const sendFileMessage = async (
    file: File,
    saveFile?: boolean,
    isPreviewFile?: boolean,
    messageText: string = '',
    srcAudio?: string,
    onCompressionProgress?: (progress: number) => void,
  ) => {
    if (
      !file ||
      !currentWorkspace?.id ||
      !selectedLeadForChatModal?.handle ||
      !currentUserId ||
      !selectedChat?.id
    ) {
      throw new Error('No se puede enviar el mensaje');
    }

    const channelType = selectedChat.integration ?? eChannelType.WHATSAPP_WEB;
    // Guard real sends before the S3 upload so a dead-channel send fails fast.
    // Skip the guard for preview-file generation (isPreviewFile): a preview is
    // uploaded to S3 for display and never dispatched to the provider, so
    // guarding it is pointless — and a blocked guard `return`s `undefined` to
    // callers that expect a tAttachment (handleOnPaste, DriveFilesAction),
    // throwing a TypeError and surfacing a misleading second toast. The real
    // (non-preview) send path is still guarded. (INT-941 review.)
    if (!isPreviewFile && isSendToReadOnlyChannelBlocked(channelType)) {
      return;
    }

    // RET-2349 (KTD5): a real send refused by plan throws, with no toast. Its
    // only real-send caller is `sendAttachmentsSequentially`, which stops,
    // keeps the tray and shows the one toast. A preview is never sent, so it
    // still uploads.
    if (!isPreviewFile && isWapiPlanSendBlocked(channelType)) {
      throw new WapiPlanSendBlockedError();
    }

    try {
      let media_type = MediaType.DOCUMENT;
      let newAttachment;

      // `isTranscodeOnlyImage` also routes HEIC/HEIF here: iPhone photos often
      // arrive with an empty or generic File.type (no `image/` prefix) on the
      // browsers this targets, which would otherwise fall through to DOCUMENT and
      // skip the transcode entirely.
      if (file.type.includes('image') || isTranscodeOnlyImage(file)) {
        media_type = MediaType.IMAGE;
      }

      if (file.type.includes('video')) {
        media_type = MediaType.VIDEO;
      }

      if (file.type.includes('audio') && !isPreviewFile) {
        media_type = MediaType.AUDIO;

        newAttachment = {
          media_type: media_type,
          mimetype: file.type,
          url: srcAudio,
          filename: file.name,
        };
      } else {
        const workspaceId = currentWorkspace?.id;
        if (!workspaceId) return;

        // Backstop for any path that did not refuse the file when it was
        // attached (BUG-676). Recorded audio never reaches this branch.
        if (isEmptyFile(file)) {
          throw new EmptyFileError(
            file.name,
            t('driveFilesAction.emptyFile', { fileName: file.name }),
          );
        }

        // Meta rejects media above per-channel byte limits (WAPI: 5MB image /
        // 16MB video) and, on WAPI, any format off its allow-list even when
        // the file is small (BUG-880). Images and videos are normalized
        // client-side to fit both; the rest keep the pre-upload rejection.
        let fileToSend = file;

        if (media_type === MediaType.IMAGE || media_type === MediaType.VIDEO) {
          // Transcode browser-undecodable images (HEIC/HEIF — iPhone photos) to
          // JPEG before anything else. This is format-driven and runs regardless
          // of size: left as HEIC the image renders as a broken/"expired" bubble
          // in Chrome/Firefox/Edge and can fail to deliver (BUG-712). Size
          // compression below then operates on the transcoded JPEG.
          if (media_type === MediaType.IMAGE) {
            // Decoding a large HEIC photo takes several seconds (libheif decodes
            // in a worker; the canvas re-encode is on the main thread) — surface
            // a processing hint so the send doesn't look stuck. willTranscodeImage
            // is cache-aware, so an already-decoded file stays silent.
            let processingAlert: tToastAlert | void = undefined;

            if (MediaCompressionService.willTranscodeImage(fileToSend)) {
              processingAlert = addAlert({
                type: eAlertType.INFO,
                message: t('driveFilesAction.processingImage', {
                  fileName: file.name,
                }),
                // The default 5s auto-dismiss is shorter than the measured 7.5s
                // 12MP baseline, so the hint would vanish mid-decode. Outlast the
                // decode and dismiss it explicitly below instead.
                duration: HEIC_TRANSCODE_TIMEOUT_MS,
              });
            }

            const normalized =
              await MediaCompressionService.normalizeImageForCompatibility(
                fileToSend,
              );

            if (processingAlert) {
              deleteAlert(processingAlert);
            }

            if (!normalized.ok) {
              LLog.error(
                {
                  component: LOG_COMPONENTS.UI,
                  method: 'Chat.actions.sendFileMessage',
                  operation: LOG_OPERATIONS.ERROR,
                },
                `HEIC transcode failed for ${file.name}`,
                new Error(normalized.reason),
                { cause: normalized.cause },
              );

              // LLog.error only writes to the browser console — it reaches no
              // backend (Sentry has no console-capture integration, and nothing
              // ships FE logs to Loki). Capture explicitly so the
              // ENCODER_UNAVAILABLE vs PROCESSING_ERROR split the rollout plan
              // depends on is actually queryable.
              sentry.captureException(
                new Error(`HEIC transcode failed: ${normalized.reason}`),
                {
                  extra: {
                    endpoint: 'Chat.actions.sendFileMessage',
                    reason: normalized.reason,
                    cause: normalized.cause,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    channelType,
                  },
                },
              );

              throw new Error(heicFailureMessage(normalized.reason, file.name));
            }

            fileToSend = normalized.file;
          }

          const { maxSize, humanLimit } = getMaxFileSizeForChannel(
            channelType,
            fileToSend,
          );
          // Judged on what will be sent: a HEIC transcoded above is a JPEG now.
          const isOversize = fileToSend.size > maxSize;

          if (isOversize && media_type === MediaType.VIDEO) {
            addAlert({
              type: eAlertType.INFO,
              message: t('driveFilesAction.compressingVideo', {
                fileName: file.name,
              }),
            });
          }

          const normalization =
            await MediaCompressionService.normalizeFileForChannel({
              file: fileToSend,
              channel: channelType,
              maxBytes: maxSize,
              onProgress: onCompressionProgress,
            });

          if (!normalization.ok) {
            LLog.error(
              {
                component: LOG_COMPONENTS.UI,
                method: 'Chat.actions.sendFileMessage',
                operation: LOG_OPERATIONS.ERROR,
              },
              `Media compression failed for ${file.name}`,
              new Error(normalization.reason),
            );

            throw new Error(
              t(
                pickNormalizationFailureKey({
                  reason: normalization.reason,
                  isOversize,
                }),
                { fileName: file.name, maxSize: humanLimit },
              ),
            );
          }

          fileToSend = normalization.file;
        }

        // The ticket carries the filename, so the storage service records the
        // file under its real name at upload time instead of under its key
        // (BUG-862). The record is not published; that is the checkbox below.
        const upload = await FileUploadService.uploadFileToS3WithRecord({
          file: fileToSend,
          workspaceId,
          name: fileToSend.name,
        }).catch((error: unknown) => {
          if (isNetworkUnreachableError(error)) {
            throw Object.assign(
              new Error(t('driveFilesAction.uploadNetworkError')),
              {
                cause: error,
              },
            );
          }
          throw error;
        });
        const response = upload.url;

        if (upload.nameRecorded === false) {
          // The object uploaded but the service could not write its record, so
          // this file will list under its key until the restore names it. Not a
          // send failure — the message goes out — but it must not be silent.
          LLog.error(
            {
              component: LOG_COMPONENTS.UI,
              method: 'Chat.actions.sendFileMessage',
              operation: LOG_OPERATIONS.ERROR,
            },
            `Storage did not record the name for ${fileToSend.name} (${upload.fileId})`,
            new Error('nameRecorded=false'),
          );
        }

        // Same helper the upload service uses for the S3 Content-Type, so the
        // stored attachment metadata can never diverge from the object it
        // describes (empty file.type derives from the extension — BUG-698).
        const canonicalMimeType = resolveEffectiveMimeType(fileToSend);

        newAttachment = {
          media_type: media_type,
          mimetype: canonicalMimeType,
          url: response,
          filename: fileToSend.name,
        };

        if (saveFile) {
          // Sharing is a second write to the record the ticket already created,
          // never a second record — the cloud callable writes its own record
          // for the same object, at its own id. Awaited here so a ticked box
          // that did not take is visible; the message still goes out either way.
          //
          // A ticket answered without `nameRecorded` wrote no record: the
          // storage service from before BUG-862 #157 ignores the name. There a
          // PATCH creates a record under the file id with no name and no type,
          // which the shared folder lists as a document called `01M4…`
          // (BUG-1067). With no record to duplicate, the callable publishes the
          // file with its name and type, as it did before #3122.
          try {
            if (upload.nameRecorded === undefined) {
              await addDocumentToWorkspaceDrive({
                workspaceID: workspaceId,
                name: fileToSend.name,
                url: response,
                mimeType: canonicalMimeType,
                type: media_type,
              });
            } else {
              await FileUploadService.shareFile(workspaceId, upload.fileId);
            }
          } catch (shareError) {
            addAlert({
              type: eAlertType.ERROR,
              message: t('uploadFileAction.saveOnDriveFailed'),
            });
            LLog.error(
              {
                component: LOG_COMPONENTS.UI,
                method: 'Chat.actions.sendFileMessage',
                operation: LOG_OPERATIONS.ERROR,
              },
              `Sharing ${fileToSend.name} to the shared folder failed`,
              shareError as Error,
            );
          }
        }
        if (isPreviewFile) {
          const previewFile: tAttachment = {
            mediaType: newAttachment.media_type,
            mimeType: newAttachment.mimetype,
            url: newAttachment.url || null,
            filename: fileToSend.name,
            vcard: null,
            decryption: null,
            duration: null,
            latitude: null,
            longitude: null,
          };

          return previewFile;
        }
      }

      const newMessage = {
        id: uuidv4(),
        channel_type: channelType,
        content: {
          text: replaceMessageWithVariable(messageText),
          attachments: [newAttachment],
          quoted: replyMessage?.id ?? undefined,
        },
        is_template: false,
        created_by: currentUserId,
        workspace_id: currentWorkspace?.id,
      };

      return sendMessageAndAnnounce(
        {
          chatId: selectedChat.id,
          data: newMessage,
        },
        currentWorkspaceId,
        selectedChat,
      );
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (error as Error).message || 'Ocurrió un error al enviar el mensaje',
      });
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
      throw error;
    } finally {
      textAreaRef.current?.focus();
    }
  };

  async function deleteFile(
    idFile: string,
    setLoading: (loading: boolean) => void,
  ) {
    setLoading(true);
    try {
      await deleteDocumentFromWorkspaceDrive({
        workspaceID: currentWorkspace?.id,
        id: idFile,
      });

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'El archivo fue eliminado exitosamente',
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (error as Error).message || 'Ocurrió un error al eliminar el archivo',
      });
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
    } finally {
      setLoading(false);
      // setUpdateChatEventsData(!updateChatEventsData);
    }
  }

  async function updateLead(
    request: tUpdateLeadRequest,
    options: {
      leadId?: string;
      /**
       * LEA-50: called when the move entered a stage mapped to a result
       * (`prompt.open_form`); the caller opens the form and the availability
       * prompt waits until it closes.
       */
      onPrompt?: (type: tCloseType) => void;
    } = {},
  ): Promise<boolean> {
    const leadId = options.leadId ?? selectedLeadForChatModal?.id;
    if (!leadId || !currentWorkspace?.id || !currentUserId) return false;
    try {
      const answer = await ChatService.updateLead(
        currentWorkspace.id,
        leadId,
        currentUserId,
        request,
      );
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Lead actualizado correctamente',
      });
      // The in-chat "Seleccionar etapa" dropdown moves a lead exactly like
      // dragging its card on the board, so it owes the same signal (RET-1350).
      // Gated on the stage key because `updateLead` is also the read-flag and
      // attribute-update path — those are not the seller working a lead.
      //
      // Probed with `in` rather than a property read because the wire field is
      // `stage_uuid` (what ChatMenu and the Pipe board both send) while
      // `tUpdateLeadRequest` = `Partial<tFunnelLeadLegacy>` only declares
      // `stage_id`. Callers pass a pre-built variable, so excess-property
      // checking never flagged the drift; reading `request.stage_uuid`
      // directly would not compile, and asserting the type would hide it.
      const prompted = options.onPrompt ? promptOf(answer) : null;
      if (prompted && options.onPrompt) {
        options.onPrompt(prompted);
      } else if ('stage_uuid' in request) {
        requestAvailabilityPrompt(eAvailabilityPromptReason.LEAD_MOVED);
      }
      return true;
    } catch (error) {
      // This is the toast the user actually sees for a failed lead save:
      // updateLead resolves `false` rather than rejecting, so a caller's
      // .catch() never runs. `(error as Error).message` on an AxiosError is
      // literally "Request failed with status code 422" (BUG-449).
      addAlert({
        type: eAlertType.ERROR,
        message: resolveApiErrorMessage(error, tErrors),
      });
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
      return false;
    }
  }

  async function archiveLead() {
    if (
      !selectedLeadForChatModal?.id ||
      !currentWorkspace?.id ||
      !currentUserId
    )
      return;
    try {
      const response = await ChatService.archiveLead(
        currentWorkspace.id,
        selectedLeadForChatModal?.id,
      );

      // Check if the operation was successful
      if (response.success) {
        addAlert({
          type: eAlertType.SUCCESS,
          message: t('archiveActions.archiveSuccess'),
        });
      } else {
        // Handle API error response (like 422)
        if (response.code === 422) {
          // Specific error for already archived/active leads
          addAlert({
            type: eAlertType.ERROR,
            message: response.message,
          });
        } else {
          // General error
          addAlert({
            type: eAlertType.ERROR,
            message: t('archiveActions.archiveError'),
          });
        }
      }
      return response;
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: t('archiveActions.archiveError'),
      });
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
      throw error;
    }
  }

  async function unarchiveLead() {
    if (
      !selectedLeadForChatModal?.id ||
      !currentWorkspace?.id ||
      !currentUserId
    )
      return;
    try {
      const response = await ChatService.unarchiveLead(
        currentWorkspace.id,
        selectedLeadForChatModal?.id,
      );

      // Check if the operation was successful
      if (response.success) {
        addAlert({
          type: eAlertType.SUCCESS,
          message: t('archiveActions.unarchiveSuccess'),
        });
      } else {
        // Handle API error response (like 422)
        if (response.code === 422) {
          // Specific error for already archived/active leads
          addAlert({
            type: eAlertType.ERROR,
            message: response.message,
          });
        } else {
          // General error
          addAlert({
            type: eAlertType.ERROR,
            message: t('archiveActions.unarchiveError'),
          });
        }
      }
      return response;
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: t('archiveActions.unarchiveError'),
      });
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
      throw error;
    }
  }

  /**
   * Blocks/unblocks the WAPI contact on WhatsApp (INT-1294). Blocking can be
   * rejected by the backend with 422 + code BLOCK_WINDOW_EXPIRED when the
   * 24-hour conversation window closed between render and click — that race
   * gets its own toast instead of the generic error.
   */
  async function setContactBlocked(blocked: boolean) {
    const handle = selectedLeadForChatModal?.handle;
    if (!handle || !currentWorkspace?.id) return;

    try {
      const response = blocked
        ? await ChatService.blockContact(currentWorkspace.id, handle)
        : await ChatService.unblockContact(currentWorkspace.id, handle);

      if (response.success) {
        // INT-1591 D1: blocking is a person dealing with the lead, like
        // archiving, so it clears the "No entregó" mark. Unblocking does not
        // bring it back. Never rejects, so the toast does not wait on it.
        if (blocked)
          void recordBlockHumanAction(currentWorkspace.id, selectedChat);
        addAlert({
          type: eAlertType.SUCCESS,
          message: blocked
            ? t('blockActions.blockSuccess')
            : t('blockActions.unblockSuccess'),
        });
      }
      return response;
    } catch (error) {
      const isWindowExpired =
        isAxiosError(error) &&
        (error.response?.data as { code?: string } | undefined)?.code ===
          'BLOCK_WINDOW_EXPIRED';

      addAlert({
        type: eAlertType.ERROR,
        message: isWindowExpired
          ? t('blockActions.blockWindowExpired')
          : blocked
            ? t('blockActions.blockError')
            : t('blockActions.unblockError'),
      });
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
      throw error;
    }
  }

  async function blockContact() {
    return setContactBlocked(true);
  }

  async function unblockContact() {
    return setContactBlocked(false);
  }

  async function decryptFile(
    attachment: tAttachment,
    chatId = selectedChat?.id,
  ) {
    try {
      const decryption = {
        keys: {
          iv: attachment.decryption?.iv ?? '',
          enc: attachment.decryption?.encryptedKey ?? '',
        },
      };
      return await downloadMessageAttachment(
        {
          url: attachment.url ?? '',
          decryption,
          mimeType: attachment.mimeType || '',
          dataUrl: attachment.url ?? '',
        },
        chatId,
      );
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
    }
  }

  async function decryptSocketFile(attachment: tChatEventContentAttachments) {
    try {
      const decryption = {
        keys: {
          iv: attachment.decryption?.keys.iv ?? '',
          enc: attachment.decryption?.keys.enc ?? '',
        },
      };
      return await downloadMessageAttachment(
        {
          url: attachment.url ?? '',
          decryption,
          mimeType: attachment.mimetype || '',
          dataUrl: attachment.url ?? '',
        },
        selectedChat?.id,
      );
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
    }
  }

  async function decryptAttachment(attachment: tAttachment) {
    if (attachment.decryption?.iv !== '') {
      const decryptedUrl = await decryptFile(attachment);
      return withDecryptedUrl(attachment, decryptedUrl ?? attachment.url);
    }
    return attachment;
  }

  async function decryptAndParseAllMessages(
    events: tChatEvent[],
    chatId = selectedChat?.id,
  ): Promise<tChatEvent[]> {
    return await Promise.all(
      events.map(async (event) => {
        if (
          event.content?.attachments &&
          event.content?.attachments?.length > 0
        ) {
          const decryptedAttachments: tAttachment[] = await Promise.all(
            event.content.attachments.map(async (attachment) => {
              if (attachment.decryption?.iv !== '') {
                const decryptedUrl = await decryptFile(attachment, chatId);
                return withDecryptedUrl(attachment, decryptedUrl ?? '');
              }
              return attachment;
            }),
          );

          const eventNew: tChatEvent = {
            ...event,
            content: {
              ...event.content,
              attachments: decryptedAttachments,
            },
          };

          return eventNew;
        }

        return event;
      }),
    );
  }

  const reSendMessage = async (toHandles: string[]) => {
    try {
      if (
        !selectedChat?.id ||
        !currentWorkspace?.id ||
        !currentUserId ||
        !selectedChat.integration
      ) {
        return;
      }

      // INT-941 / U3 — `forwardEvent` is a separate endpoint reachable from the
      // message-bubble context menu with zero composer involvement, so the U2
      // read-only composer does not cover it; guard the forward path here.
      // `selectedChat.integration` is already defined past the falsy guard
      // above, so this catches a defined-but-read-only WHATSAPP_WEB channel.
      if (isSendToReadOnlyChannelBlocked(selectedChat.integration)) {
        return;
      }

      // RET-2339 (D6): a forward out of a WhatsApp API chat whose plan has no
      // WhatsApp API never reaches `forwardEvent`.
      if (blockWapiPlanSend(selectedChat.integration)) {
        return;
      }

      const payload = {
        workspaceId: currentWorkspace.id,
        channelType: selectedChat.integration,
        toHandles: toHandles,
        createdBy: currentUserId,
      };

      const refusals: tOutboundRefusal[] = [];
      for (const eventId of resendIds) {
        const response = await ChatService.forwardEvent(
          selectedChat.id,
          eventId,
          payload,
        );
        response?.results?.forEach((destination) => {
          const refusal = lookupOutboundRefusalByCode(destination.error_code);
          if (refusal) refusals.push(refusal);
        });
      }

      // forward-v2 answers 200 whatever happens to each destination, and a
      // refused destination only shows its failed row inside that other chat.
      // Say it here, once, while the seller is still on this one. The paused
      // copy outranks the neutral one: it is the reason the seller can act on.
      const refusal =
        refusals.find((candidate) => candidate.kind === 'paused') ??
        refusals[0];
      if (refusal) {
        addAlert(buildOutboundRefusalAlert(refusal, tErrors));
      }
    } catch (error) {
      const refusal = lookupOutboundRefusal(error);
      addAlert(
        refusal
          ? buildOutboundRefusalAlert(refusal, tErrors)
          : {
              type: eAlertType.ERROR,
              message: `Ocurrió un error al reenviar el mensaje: ${(error as Error).message}`,
            },
      );
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
    }
  };

  // NOTE (BUG-712): this re-sends an already-uploaded drive file by URL and does
  // not transcode. A HEIC saved to the drive before this fix still ships as-is;
  // the ImageContent honest-copy fallback backstops it, and new drive saves now
  // store the transcoded JPEG (sendFileMessage writes fileToSend). Transcoding a
  // remote drive HEIC would need fetch+decode+re-upload — out of scope here.
  async function sendDriveFile(file: tDriveFile, fileText?: string) {
    // RET-2349 (KTD5): refused by plan before the `try`, so the catch below
    // neither toasts nor logs it. The attachment loop that calls this shows
    // the one toast and keeps the tray.
    if (isWapiPlanSendBlocked(selectedChat?.integration)) {
      throw new WapiPlanSendBlockedError();
    }

    try {
      if (
        !selectedChat?.id ||
        !currentWorkspace?.id ||
        !currentUserId ||
        !selectedChat.integration
      ) {
        return;
      }

      // INT-941 / U3 — `selectedChat.integration` is already defined past the
      // falsy guard above, so this catches a defined-but-read-only
      // WHATSAPP_WEB channel (not the undefined `?? WHATSAPP_WEB` case).
      if (isSendToReadOnlyChannelBlocked(selectedChat.integration)) {
        return;
      }

      const mediaType =
        (file.mimeType.includes('image') && MediaType.IMAGE) ||
        (file.mimeType.includes('video') && MediaType.VIDEO) ||
        (file.mimeType.includes('audio') && MediaType.AUDIO) ||
        MediaType.DOCUMENT;

      const newMessage = {
        id: uuidv4(),
        channel_type: selectedChat.integration,
        content: {
          quoted: replyMessage?.id ?? undefined,
          attachments: [
            {
              media_type: mediaType,
              mimetype: file.mimeType,
              url: file.url,
              filename: file.name,
            },
          ],
          ...(fileText && { text: replaceMessageWithVariable(fileText) }),
        },
        is_template: false,
        created_by: currentUserId,
        workspace_id: currentWorkspace?.id,
      };

      return await sendMessageAndAnnounce(
        {
          chatId: selectedChat?.id,
          data: newMessage,
        },
        currentWorkspaceId,
        selectedChat,
      );
    } catch (error) {
      // A refused send reaches the seller once, from the attachment flow that
      // called this (sendAttachmentsSequentially stops at the first refusal).
      // A toast here as well would stack one per file.
      if (!lookupOutboundRefusal(error)) {
        addAlert({
          type: eAlertType.ERROR,
          message:
            (error as Error).message || 'Ocurrió un error al enviar el mensaje',
        });
      }
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
      throw error;
    }
  }

  const sendMetaTemplateFbIg = async (message: string) => {
    if (
      !message ||
      !currentWorkspace?.id ||
      !selectedLeadForChatModal?.handle ||
      !currentUserId ||
      !selectedChat
    ) {
      throw new Error('No se puede enviar la plantilla');
    }
    const channelType = selectedChat.integration ?? eChannelType.WHATSAPP_WEB;
    if (isSendToReadOnlyChannelBlocked(channelType)) {
      return;
    }
    // RET-2349 (R1): no send out of a WhatsApp API chat whose plan has no
    // WhatsApp API. One toast, then return without sending.
    if (blockWapiPlanSend(channelType)) {
      return;
    }
    try {
      const newMessage = {
        id: uuidv4(),
        channel_type: channelType,
        content: {
          text: replaceMessageWithVariable(message),
          quoted: replyMessage?.id ?? undefined,
        },
        is_template: true,
        created_by: currentUserId,
        workspace_id: currentWorkspace?.id,
      };

      await sendMessageAndAnnounce(
        {
          chatId: selectedChat?.id,
          data: newMessage,
        },
        currentWorkspaceId,
        selectedChat,
      );
      if (replyMessage?.id) {
        setReplyMessage(null);
      }
      setMessageBarValue('');
    } catch (error) {
      addAlert(
        sendFailureAlert({
          error,
          fallback: 'Ocurrió un error al enviar la plantilla',
        }),
      );
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'Chat.actions',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in Chat.actions',
        error as unknown as Error,
      );
    } finally {
      textAreaRef.current?.focus();
    }
  };

  return {
    sendTextMessage,
    sendTikTokRichMessage,
    sendLocationMessage,
    sendAudioMessage,
    sendFileMessage,
    updateLead,
    archiveLead,
    unarchiveLead,
    blockContact,
    unblockContact,
    decryptAndParseAllMessages,
    decryptAttachment,
    decryptSocketFile,
    reSendMessage,
    sendDriveFile,
    deleteFile,
    sendMetaTemplateFbIg,
  };
}
